import { Search, X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Flush, edge-to-edge toolbar strip used directly under the breadcrumb on workspace
 * list pages (Shifts, Attendance, Penalties, …). Reproduces the Shifts-page chrome:
 * a single row holding the company filter, an optional inline search, and page actions.
 *
 * Pair with `<PageShell hideHeader className="gap-0">` so the strip sits flush against
 * the page top. The negative margins cancel PageShell's content padding so the bar spans
 * the full width, matching the responsive gutters (`-mx-4 / sm:-mx-5 / lg:-mx-6`).
 */
export interface WorkspaceToolbarProps {
  /** Leading controls, typically the company filter (`<WorkspaceCompanyChips />`). */
  filter?: React.ReactNode
  /** Trailing controls, right-aligned (primary actions like "Issue penalty"). */
  actions?: React.ReactNode
  /** Toolbar body — usually a search field via `<WorkspaceToolbarSearch />`. */
  children?: React.ReactNode
  className?: string
}

export function WorkspaceToolbar({
  filter,
  actions,
  children,
  className,
}: WorkspaceToolbarProps): React.ReactElement {
  return (
    <section
      className={cn(
        '-mx-4 -mt-4 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6',
        className
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 xl:flex-nowrap xl:gap-6">
        {filter ? <div className="shrink-0">{filter}</div> : null}
        {children ? (
          // `basis-full`, not `flex-1`: a 0% basis lets the search shrink and
          // share the first row below xl, which clipped it off-screen on phones.
          // A full basis forces it onto its own line until the row has space.
          <div className="order-last w-full min-w-0 grow basis-full xl:order-none xl:mx-auto xl:max-w-2xl xl:basis-auto">
            {children}
          </div>
        ) : null}
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
    </section>
  )
}

WorkspaceToolbar.displayName = 'WorkspaceToolbar'

/**
 * Standard search field for {@link WorkspaceToolbar}: leading magnifier, clear button,
 * and the same 9-tall rounded control used on the Shifts page. Controlled — the parent
 * owns the value and debouncing.
 */
export interface WorkspaceToolbarSearchProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel?: string
  /** Highlight the field (e.g. when a debounced query is active). */
  active?: boolean
  className?: string
}

export function WorkspaceToolbarSearch({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel = 'Search',
  active = false,
  className,
}: WorkspaceToolbarSearchProps): React.ReactElement {
  return (
    <div className={cn('relative', className)}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
        aria-hidden
      />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          'h-9 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-9 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500',
          active && 'border-primary/40 ring-2 ring-primary/10'
        )}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          aria-label={`Clear ${ariaLabel.toLowerCase()}`}
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  )
}

WorkspaceToolbarSearch.displayName = 'WorkspaceToolbarSearch'
